// THE ONE CHECK-IN FORM (Aug, Mark: "combine the check in experiences...
// the been draw to be the standard" + "It should stay on the same page you
// are on"). Extracted from BeenScreen so it can OVERLAY any page — map,
// profile, a results board — with no tab switch. Four doors, one form:
//   venue card "Check in"  → venue prefilled, Right now selected
//   the + menu "Check in"  → empty form, search open, Right now selected
//   Been "+ Add"           → date mode (yesterday)
//   scheduler album prompt → date mode, plan's date+time, invitees riding
// PRESENCE IS THE TOGGLE: "Show on live map" (default off) is the only
// thing that puts anyone on live surfaces — never the timestamp.
//
// Mounted by App when `prefill` exists; unmounts on close, so state
// initializers read the prefill directly (fresh mount per open).
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { MapPin, Home } from "lucide-react";
import { supabase } from "../supabaseClient";
import { sendPush } from "../lib/push";
import {
  searchPlaces,
  addGooglePlace,
  createPersonalPlace,
} from "../lib/venueSearch";

function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

export function CheckinForm({ userId, prefill, onClose, onCreated, showToast }) {
  const todayStr = localDateStr();
  // EVENT VARIANT (Aug 30) — same form, deliberate tier. Date-only future,
  // Collect photos + Open guest list toggles (both born ON), NO Who (Mark:
  // dropped until accepting can honestly mean attending), NO live-map toggle
  // (presence is a choice made on the night, never at planning time).
  const isEvent = prefill?.event === true;
  const [addLabel, setAddLabel] = useState("");
  const [addQ, setAddQ] = useState("");
  const [addResults, setAddResults] = useState([]);
  const [addGoogle, setAddGoogle] = useState([]);
  const [addingPlaceId, setAddingPlaceId] = useState(null);
  const [addVenues, setAddVenues] = useState(() =>
    prefill?.venue ? [prefill.venue] : []
  );
  const [addSearching, setAddSearching] = useState(() => !prefill?.venue);
  const [addBlurred, setAddBlurred] = useState(false);
  const [addMode, setAddMode] = useState(() =>
    prefill?.mode === "now" && prefill?.event !== true ? "now" : "date"
  );
  const [addDate, setAddDate] = useState(() =>
    prefill?.date
      ? prefill.date
      : prefill?.mode === "now" || prefill?.event === true
      ? localDateStr()
      : localDateStr(new Date(Date.now() - 24 * 60 * 60 * 1000))
  );
  const [addTime, setAddTime] = useState(() => prefill?.time || "");
  // THE WHEN-PILL (Oct 10, Mark — the form was scrolling): the door
  // pre-answered "when?", so the form shows ONE pill with the answer and
  // a "change" affordance that opens the old controls. Events keep their
  // date inputs always visible (date is the event's whole point).
  const [whenEdit, setWhenEdit] = useState(false);
  function whenPillLabel() {
    if (addMode === "now") return "Right now";
    if (!addDate) return "Pick a date";
    const d = new Date(`${addDate}T${addTime || "19:00"}`);
    const day = d.toLocaleDateString("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    if (addDate < localDateStr()) return day; // past: no clock time shown
    const t = d.toLocaleTimeString("en-AU", {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${day} · ${t}`;
  }
  const [addShowLive, setAddShowLive] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  // WHO? (Aug 21, Mark: "should we ask users to add their friends at this
  // level, rather than downstream?") — the old CheckinSheet asked at
  // check-in time; the unified form lost it. Optional friend chips; the
  // selected ride the existing invitee pipeline (consent tag + push).
  // Session-door invitees arrive pre-filled and merge in.
  const [friends, setFriends] = useState(null); // null = loading
  const [whoIds, setWhoIds] = useState(() => new Set(prefill?.invitees || []));
  const [whoQ, setWhoQ] = useState(""); // adaptive search (>8 friends)
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data: fr } = await supabase
        .from("friendships")
        .select("requester_id, addressee_id")
        .eq("status", "accepted")
        .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
      const ids = Array.from(
        new Set(
          (fr || []).map((f) =>
            f.requester_id === userId ? f.addressee_id : f.requester_id
          )
        )
      );
      if (ids.length === 0) {
        if (!cancelled) setFriends([]);
        return;
      }
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url")
        .in("id", ids)
        .order("display_name", { ascending: true });
      if (!cancelled) setFriends(profs || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);
  function toggleWho(id) {
    setWhoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  const addInvitees = Array.from(whoIds);
  // EVENT toggle — Collect photos maps to is_album (the event's album is
  // the point). "Open guest list" was REMOVED Aug 30 (Mark: "not at all
  // good for people not on it") — it governed a Flanit-only door that
  // wasn't built; it returns with the guest package (anon invite + RSVP +
  // public ?when= page + cover), all at once or not at all. The card's
  // settings toggle still controls guests_can_invite on any night.
  // Oct 10 (Mark): the toggle now lives on EVERY tense's form, not just
  // events. Events keep default ON (the organiser came for it); casual
  // nights default OFF (the album stays opt-in).
  const [collectPhotos, setCollectPhotos] = useState(
    () => prefill?.event === true
  );
  // PERSONAL ADDRESS (Aug 30, Mark: "What if it's a personal address?") —
  // suburb goes on the personal venue row (world sees "Mark's house party ·
  // Fitzroy" and nothing more); the street address goes to
  // activity_private_place, readable only by the guest list. Never on venues:
  // venue reads are open by design (personal_places.sql).
  const [placeSuburb, setPlaceSuburb] = useState("");
  const [placeAddress, setPlaceAddress] = useState("");
  const [suburbTouched, setSuburbTouched] = useState(false);
  const personalVenue =
    addVenues[0]?.source === "personal" ? addVenues[0] : null;
  // BORN-ALBUM (Aug 21): the scheduler door creates the album directly —
  // its own popup already asked, so the post-save prompt must not double-ask.
  // Events fold in: the Collect photos toggle IS the album answer.
  const bornAlbum = prefill?.album === true || collectPhotos;

  useEffect(() => {
    const q = addQ.trim();
    if (q.length < 2) {
      setAddResults([]);
      setAddGoogle([]);
      return;
    }
    const t = setTimeout(async () => {
      const { venues, google } = await searchPlaces(q, userId);
      setAddResults(venues);
      setAddGoogle(google);
    }, 250);
    return () => clearTimeout(t);
  }, [addQ, userId]);

  function pickVenue(v) {
    if (!v) return;
    setAddVenues((prev) =>
      prev.some((x) => x.id === v.id) ? prev : [...prev, v]
    );
    if (v.source === "personal" && v.suburb) setPlaceSuburb(v.suburb);
    setAddSearching(false);
    setAddQ("");
    setAddResults([]);
    setAddGoogle([]);
  }

  async function pickGooglePlace(r) {
    if (addingPlaceId) return;
    setAddingPlaceId(r.place_id);
    try {
      pickVenue(await addGooglePlace(r.place_id));
    } catch (e) {
      console.error("Google check-in pick failed:", e);
      showToast?.("Couldn't add that place");
    }
    setAddingPlaceId(null);
  }

  async function confirmAddNight() {
    const first = addVenues[0];
    if (!first || addSaving) return;
    if (addMode === "date" && !addDate) return;
    setAddSaving(true);
    // "Right now" = the actual clock time. A dated TODAY/FUTURE night lands
    // at its chosen time (default 7pm); a PAST night lands at 8pm, inside
    // the ±12h same-night window.
    const isNow = addMode === "now";
    let ts;
    if (isNow) ts = new Date();
    else if (addDate >= todayStr)
      ts = new Date(`${addDate}T${addTime || "19:00"}:00`);
    else ts = new Date(`${addDate}T20:00:00`);
    // Events are never live at creation — the choice becomes honest on the
    // night, from the card, not three weeks out.
    const liveEligible =
      !isEvent && (isNow || (addMode === "date" && addDate >= todayStr));
    const showLive = liveEligible && addShowLive;
    const W = 12 * 60 * 60 * 1000;
    // Already have a check-in that night? Open it instead of a dupe twin.
    const { data: existing } = await supabase
      .from("activities")
      .select("id, created_at, label")
      .eq("user_id", userId)
      .eq("venue_id", first.id)
      .eq("kind", "checkin")
      .gte("created_at", new Date(ts.getTime() - W).toISOString())
      .lte("created_at", new Date(ts.getTime() + W).toISOString())
      .limit(1);
    const label = addLabel.trim() || null;
    let act = existing?.[0] || null;
    // Reopening an existing plain night through the album door (scheduler)
    // or the event door flips the tier up — own row, plain update.
    if (act && (bornAlbum || isEvent)) {
      const up = {};
      if (bornAlbum) up.is_album = true;
      if (isEvent) up.is_event = true;
      await supabase.from("activities").update(up).eq("id", act.id);
    }
    if (act && label && !act.label) {
      const { error: lblErr } = await supabase
        .from("activities")
        .update({ label })
        .eq("id", act.id);
      if (lblErr) console.error("Check-in label update failed:", lblErr);
      else act = { ...act, label };
    }
    if (!act) {
      const { data: inserted, error } = await supabase
        .from("activities")
        .insert({
          user_id: userId,
          kind: "checkin",
          venue_id: first.id,
          label,
          created_at: ts.toISOString(),
          show_live: showLive,
          is_album: bornAlbum, // night-level flag lives on the root
          is_event: isEvent,
          // guests_can_invite deliberately NOT written — the DB default
          // rules until the guest package lands; the card's settings
          // toggle remains the control.
        })
        .select("id, created_at, label")
        .single();
      if (error) {
        console.error("Check-in failed:", error);
        showToast?.("Couldn't save. Try again");
        setAddSaving(false);
        return;
      }
      act = inserted;
    }

    // PERSONAL PLACE extras. Suburb → the venue row (own personal rows are
    // updatable; policy allows suburb, still never an address). Street
    // address → activity_private_place on the ROOT, guest-list-only by RLS.
    if (personalVenue) {
      const sub = placeSuburb.trim().slice(0, 40);
      if (sub && sub !== (personalVenue.suburb || "")) {
        const { error: subErr } = await supabase
          .from("venues")
          .update({ suburb: sub })
          .eq("id", personalVenue.id);
        if (subErr) console.error("Suburb update failed:", subErr);
      }
      const addr = placeAddress.trim().slice(0, 200);
      if (addr) {
        const { error: addrErr } = await supabase
          .from("activity_private_place")
          .upsert({ activity_id: act.id, address: addr });
        if (addrErr) console.error("Private address save failed:", addrErr);
      }
    }

    // AUTO-MINT the collect link for events with photos on (Aug 30, Mark:
    // the link was buried in settings). An event that toggled Collect
    // photos wants the link by definition — the card-face signpost reads
    // "Share" from birth instead of asking the owner to find a Create
    // button. Casual albums stay deliberate. Failure is silent: settings
    // can still mint.
    if (collectPhotos && act?.id) {
      const { data: existingLink } = await supabase
        .from("checkin_collect_links")
        .select("id")
        .eq("activity_id", act.id)
        .eq("revoked", false)
        .maybeSingle();
      if (!existingLink) {
        const token = (
          crypto.randomUUID?.() ||
          `${Date.now()}${Math.random().toString(36).slice(2)}`
        ).replace(/-/g, "");
        const { error: linkErr } = await supabase
          .from("checkin_collect_links")
          .insert({ activity_id: act.id, token });
        if (linkErr) console.error("Event link auto-mint failed:", linkErr);
      }
    }

    // The trail: legs point at the root via joined_from — the cluster folds
    // them into one card, one album, one guest list (July 31 doctrine).
    const rest = addVenues.slice(1);
    if (rest.length > 0) {
      const legs = rest.map((v, i) => ({
        user_id: userId,
        kind: "checkin",
        venue_id: v.id,
        label: null,
        joined_from: act.id,
        created_at: new Date(
          new Date(act.created_at).getTime() + (i + 1) * 60 * 60 * 1000
        ).toISOString(),
        show_live: showLive, // one night, one visibility choice
      }));
      const { error: legErr } = await supabase.from("activities").insert(legs);
      if (legErr) {
        console.error("Check-in legs failed:", legErr);
        showToast?.("Added the night, but some places didn't save");
      }
    }

    // AUTO-INVITE the session's people (Aug, Mark) — pending tag + push on
    // the ROOT; 23505 (already tagged / self-requested) left alone.
    if (addInvitees.length > 0) {
      const future = new Date(act.created_at).getTime() > Date.now();
      const dateTxt = new Date(act.created_at).toLocaleDateString("en-AU", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
      for (const uid of addInvitees) {
        if (!uid || uid === userId) continue;
        const { error: tagErr } = await supabase
          .from("activity_tags")
          .insert({ activity_id: act.id, tagged_user_id: uid });
        if (!tagErr) {
          sendPush(
            uid,
            future ? "You're on the plan 🎉" : "You've been checked in",
            future
              ? `${label || first.name} · ${dateTxt}. Accept to join the night`
              : `${label || first.name}. Accept to add it to your Been list`
          );
        } else if (tagErr.code !== "23505") {
          console.error("Auto-invite failed:", tagErr);
        }
      }
    }

    onCreated?.({
      activityId: act.id,
      ownerId: userId,
      ownerName: "You",
      ownerProfile: null,
      venueName: first.name,
      label: act.label || null,
      venueObj: first,
      timestamp: act.created_at,
      bornAlbum, // App skips the album prompt when the door already asked
      isEvent, // App opens the card as the share moment
      // Oct 10 (Mark): the Collect photos toggle is the intent signal —
      // ON means they came for the link, so the card opens straight onto
      // settings where the auto-minted link + QR are waiting.
      openSettings: isEvent && collectPhotos,
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-[4000]">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        className="absolute left-0 right-0 top-12 mx-auto max-w-sm bg-white rounded-3xl shadow-2xl overflow-y-auto p-5"
        style={{ width: "calc(100% - 1.5rem)", maxHeight: "calc(100% - 130px)" }}
      >
        <div className="flex items-start justify-between mb-1">
          {/* TITLES ARE THE QUESTION IN TENSE (Mark, Oct 10) — "check in"
              retired from user-facing copy; the title morphs with the
              when-pill like everything else. */}
          <p className="text-sm font-semibold">
            {isEvent
              ? "Create an event"
              : addMode === "now"
              ? "What's happening"
              : addDate >= todayStr
              ? "What's the plan"
              : "What happened"}
          </p>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="-mt-1 -mr-1 w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-neutral-500 hover:bg-neutral-100"
          >
            ✕
          </button>
        </div>
        {/* Intro blurb: events only (Oct 10 — the casual form's two-line
            explainer was costing the Done button its place on screen). */}
        {isEvent && (
          <p className="text-[11px] text-neutral-500 mb-3">
            The album's ready before the night. Anyone can add photos with
            your link, even without Flanit.
          </p>
        )}
        {/* What / Where / When (Mark, Aug 20) — three questions, no more
            words. Also settles audit #4: flat labels beat mode-aware ones. */}
        <label className="block text-[11px] font-medium text-neutral-500 mb-1 px-1">
          What?
        </label>
        <input
          value={addLabel}
          onChange={(e) => setAddLabel(e.target.value)}
          placeholder="Renasha's birthday (optional)"
          maxLength={80}
          className="w-full rounded-full border border-neutral-200 px-4 py-2.5 text-base focus:outline-none focus:border-[#455d3b] mb-3"
        />

        <label className="block text-[11px] font-medium text-neutral-500 mb-1 px-1">
          Where?
        </label>
        {addVenues.map((v, i) => (
          <div
            key={v.id}
            className="flex items-center gap-2.5 rounded-xl bg-neutral-50 px-3 py-2.5 mb-1.5"
          >
            <MapPin size={15} className="shrink-0 text-[#455d3b]" />
            <span className="flex-1 min-w-0 truncate text-sm font-medium text-neutral-900">
              {v.name}
            </span>
            {i > 0 && (
              <span className="text-[11px] text-neutral-400 shrink-0">then</span>
            )}
            <button
              type="button"
              aria-label={`Remove ${v.name}`}
              onClick={() =>
                setAddVenues((prev) => prev.filter((x) => x.id !== v.id))
              }
              className="text-xs text-neutral-500 underline shrink-0"
            >
              Remove
            </button>
          </div>
        ))}
        {!addSearching ? (
          <button
            type="button"
            onClick={() => setAddSearching(true)}
            className="mt-1 mb-1 text-xs text-[#455d3b] underline underline-offset-2"
          >
            + Add another location
          </button>
        ) : (
          <>
            <input
              value={addQ}
              onChange={(e) => {
                setAddQ(e.target.value);
                setAddBlurred(false);
              }}
              onBlur={() => setAddBlurred(true)}
              placeholder="Search for the place, or type your own"
              className="w-full rounded-full border border-neutral-200 px-4 py-2.5 text-base focus:outline-none focus:border-[#455d3b]"
            />
            {addVenues.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  setAddSearching(false);
                  setAddQ("");
                  setAddResults([]);
                  setAddGoogle([]);
                }}
                className="mt-1.5 text-xs text-neutral-500 underline underline-offset-2"
              >
                Cancel
              </button>
            )}
            <div className="mt-2 space-y-1">
              {addResults.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => pickVenue(v)}
                  className="w-full flex items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-neutral-50 active:scale-[0.99] transition"
                >
                  <MapPin size={15} className="shrink-0 text-neutral-400" />
                  <span className="flex-1 min-w-0 truncate text-sm text-neutral-800">
                    {v.name}
                  </span>
                  {v.suburb && (
                    <span className="text-[11px] text-neutral-400 shrink-0">
                      {v.suburb}
                    </span>
                  )}
                </button>
              ))}
              {addGoogle.length > 0 && (
                <>
                  <p className="px-2 pt-2 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                    More places
                  </p>
                  {addGoogle.map((r) => (
                    <button
                      key={r.place_id}
                      type="button"
                      disabled={!!addingPlaceId}
                      onClick={() => pickGooglePlace(r)}
                      className="w-full flex items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-neutral-50 active:scale-[0.99] transition disabled:opacity-50"
                    >
                      <MapPin size={15} className="shrink-0 text-neutral-300" />
                      <span className="flex-1 min-w-0 truncate text-sm text-neutral-800">
                        {r.name}
                      </span>
                      <span className="text-[11px] text-neutral-400 shrink-0 truncate max-w-[110px]">
                        {addingPlaceId === r.place_id ? "Adding…" : r.address || ""}
                      </span>
                    </button>
                  ))}
                </>
              )}
              {addQ.trim().length >= 2 && (
                <div
                  className={`mt-2 rounded-xl border p-2.5 transition ${
                    addBlurred && addResults.length === 0
                      ? "border-[#455d3b] bg-[#edf2eb]"
                      : "border-neutral-200 bg-white"
                  }`}
                >
                  <button
                    type="button"
                    disabled={addingPlaceId === "personal"}
                    onClick={async () => {
                      setAddingPlaceId("personal");
                      const v = await createPersonalPlace(addQ, userId);
                      setAddingPlaceId(null);
                      if (v) pickVenue(v);
                      else showToast?.("Couldn't add that place");
                    }}
                    className="w-full flex items-center gap-2.5 text-left active:scale-[0.99] transition disabled:opacity-50"
                  >
                    <Home size={15} className="shrink-0 text-[#455d3b]" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-neutral-900 truncate">
                        Use "{addQ.trim()}"
                      </span>
                      <span className="block text-[11px] text-neutral-500">
                        A place of your own — just a name. No address, and it
                        won't appear on the map.
                      </span>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* PERSONAL PLACE extras (Aug 30) — suburb is the only location the
            world ever sees ("Mark's house party · Fitzroy"); the street
            address is event-only and guest-list-only. */}
        {personalVenue && (
          <div className="mt-1 mb-1">
            {isEvent && (
              <>
                <input
                  value={placeAddress}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPlaceAddress(val);
                    // Guess the suburb from the last comma part until edited.
                    if (!suburbTouched) {
                      const parts = val.split(",");
                      if (parts.length > 1)
                        setPlaceSuburb(parts[parts.length - 1].trim());
                    }
                  }}
                  placeholder="Street address (optional)"
                  maxLength={200}
                  className="w-full rounded-full border border-neutral-200 px-4 py-2.5 text-base focus:outline-none focus:border-[#455d3b] mb-1"
                />
                <p className="mb-2 px-1 text-[10px] text-neutral-400">
                  Only your guest list ever sees the address.
                </p>
              </>
            )}
            <input
              value={placeSuburb}
              onChange={(e) => {
                setSuburbTouched(true);
                setPlaceSuburb(e.target.value);
              }}
              placeholder="Suburb (optional)"
              maxLength={40}
              className="w-full rounded-full border border-neutral-200 px-4 py-2.5 text-base focus:outline-none focus:border-[#455d3b] mb-1"
            />
            <p className="mb-1 px-1 text-[10px] text-neutral-400">
              Friends only ever see the suburb.
            </p>
          </div>
        )}

        {/* WHO? — optional; selected friends get the consent tag + push on
            save. ADAPTIVE (the July CheckinSheet rule, Mark's Aug 21 note:
            a chip wall "isn't great for users with a lot of friends"):
            ≤8 friends = all chips; more = a search box that filters as you
            type. Selected always render as solid chips up top. EVENTS skip
            Who entirely (Aug 30 ruling) — invites come later, from the card,
            once accepting can mean attending. */}
        {!isEvent && friends && friends.length > 0 && (
          <>
            <label className="mt-3 block text-[11px] font-medium text-neutral-500 mb-1 px-1">
              Who? <span className="text-neutral-400">(optional)</span>
            </label>
            {whoIds.size > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {friends
                  .filter((f) => whoIds.has(f.id))
                  .map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleWho(f.id)}
                      className="rounded-full bg-[#455d3b] px-3 py-1.5 text-xs font-medium text-white transition active:scale-95"
                    >
                      {(f.display_name || "Friend").split(" ")[0]} ✕
                    </button>
                  ))}
              </div>
            )}
            {friends.length > 8 && (
              <input
                value={whoQ}
                onChange={(e) => setWhoQ(e.target.value)}
                placeholder="Search friends"
                className="mb-1.5 w-full rounded-full border border-neutral-200 px-4 py-2.5 text-base focus:outline-none focus:border-[#455d3b]"
              />
            )}
            {(friends.length <= 8 || whoQ.trim().length > 0) && (
              <div className="flex flex-wrap gap-1.5 mb-1">
                {friends
                  .filter(
                    (f) =>
                      !whoIds.has(f.id) &&
                      (friends.length <= 8 ||
                        (f.display_name || "")
                          .toLowerCase()
                          .includes(whoQ.trim().toLowerCase()))
                  )
                  .slice(0, 12)
                  .map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => {
                        toggleWho(f.id);
                        setWhoQ("");
                      }}
                      className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 transition active:scale-95"
                    >
                      {(f.display_name || "Friend").split(" ")[0]}
                    </button>
                  ))}
              </div>
            )}
            {whoIds.size > 0 && (
              <p className="mb-2 px-1 text-[10px] text-neutral-400">
                They'll be asked before their friends see anything.
              </p>
            )}
          </>
        )}

        <label className="mt-3 block text-[11px] font-medium text-neutral-500 mb-1 px-1">
          When?
        </label>
        {/* THE WHEN-PILL (Oct 10): one row showing the door's answer;
            "change" opens the full controls. Doctrine's tense-morph —
            picking a different date quietly becomes the other tense's
            form. The "upcoming night" hint line retired with it. */}
        {!isEvent && !whenEdit && (
          <div className="mb-2 flex gap-2">
            <span className="flex-1 flex items-center justify-center rounded-full border border-[#455d3b] bg-[#edf2eb] px-4 py-2.5 text-sm font-medium text-[#455d3b]">
              {whenPillLabel()}
            </span>
            <button
              type="button"
              onClick={() => setWhenEdit(true)}
              className="shrink-0 rounded-full border border-neutral-200 px-4 py-2.5 text-sm text-neutral-500 active:scale-95 transition"
            >
              change
            </button>
          </div>
        )}
        {!isEvent && whenEdit && (
          <div className="mb-2 flex bg-neutral-100 rounded-full p-0.5 text-sm font-medium">
            <button
              type="button"
              onClick={() => setAddMode("now")}
              className={`flex-1 rounded-full py-2.5 transition ${
                addMode === "now"
                  ? "bg-white text-[#455d3b] shadow-sm"
                  : "text-neutral-500"
              }`}
            >
              Right now
            </button>
            <button
              type="button"
              onClick={() => setAddMode("date")}
              className={`flex-1 rounded-full py-2.5 transition ${
                addMode === "date"
                  ? "bg-white text-[#455d3b] shadow-sm"
                  : "text-neutral-500"
              }`}
            >
              Choose date
            </button>
          </div>
        )}
        {(isEvent || whenEdit) && addMode === "date" && (
          <div className="mb-2 flex gap-2">
            <input
              type="date"
              min={isEvent ? todayStr : undefined}
              value={addDate}
              onChange={(e) => setAddDate(e.target.value)}
              className="flex-1 min-w-0 appearance-none bg-white text-left rounded-full border border-neutral-200 px-4 py-2.5 text-base focus:outline-none focus:border-[#455d3b]"
            />
            {addDate >= todayStr && (
              <input
                type="time"
                value={addTime || "19:00"}
                onChange={(e) => setAddTime(e.target.value)}
                className="w-28 shrink-0 appearance-none bg-white text-left rounded-full border border-neutral-200 px-3 py-2.5 text-base focus:outline-none focus:border-[#455d3b]"
              />
            )}
          </div>
        )}
        {isEvent ? (
          <>
            {/* Collect photos (default ON). No live-map toggle (events are
                never live at creation) and no guest-list toggle (removed
                Aug 30 — returns with the guest package). */}
            <div className="mb-3 rounded-2xl border border-[#c5d4c2] bg-[#edf2eb]/40 px-3.5 py-2.5">
              <div className="flex items-center gap-3">
                <span className="flex-1">
                  <span className="block text-sm font-medium text-neutral-800">
                    Collect photos
                  </span>
                  <span className="block text-[11px] text-neutral-500">
                    One album for the night, link and QR included
                  </span>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={collectPhotos}
                  onClick={() => setCollectPhotos((v) => !v)}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                    collectPhotos ? "bg-[#455d3b]" : "bg-neutral-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                      collectPhotos ? "left-[22px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* ONE TOGGLE BOX (Oct 10, Mark — the form was scrolling): live
             row only on live tenses, collect row on all three, sublabels
             render ONLY when a toggle is on. */
          <div className="mb-3 rounded-2xl border border-neutral-200 overflow-hidden">
            {(addMode === "now" ||
              (addMode === "date" && addDate >= todayStr)) && (
              <div className="px-3.5 py-2.5">
                <div className="flex items-center gap-3">
                  <span className="flex-1 text-sm font-medium text-neutral-800">
                    {addMode === "now"
                      ? "Show on live map"
                      : "Show on live map when it starts"}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={addShowLive}
                    onClick={() => setAddShowLive((v) => !v)}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                      addShowLive ? "bg-[#455d3b]" : "bg-neutral-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                        addShowLive ? "left-[22px]" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
                {addShowLive && (
                  <p className="mt-1 text-[11px] text-neutral-500">
                    {addMode === "now"
                      ? `Friends see you're at ${
                          addVenues[0]?.name || "the spot"
                        } now`
                      : `Friends see you at ${
                          addVenues[0]?.name || "the spot"
                        } from ${addTime || "19:00"} on the day`}
                  </p>
                )}
              </div>
            )}
            <div className="border-t border-neutral-100 px-3.5 py-2.5 first:border-t-0">
              <div className="flex items-center gap-3">
                <span className="flex-1 text-sm font-medium text-neutral-800">
                  Collect photos
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={collectPhotos}
                  onClick={() => setCollectPhotos((v) => !v)}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition ${
                    collectPhotos ? "bg-[#455d3b]" : "bg-neutral-300"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                      collectPhotos ? "left-[22px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
              {/* The "not on Flanit" line stays in BOTH states (Oct 10,
                  Mark — it vanished when sublabels went on-only). */}
              <p className="mt-1 text-[11px] text-neutral-500">
                {collectPhotos
                  ? "A link and QR for people not on Flanit. You'll find them in the card's settings"
                  : "For people who aren't on Flanit"}
              </p>
            </div>
          </div>
        )}
        <button
          type="button"
          disabled={
            addSaving ||
            addVenues.length === 0 ||
            (addMode === "date" && !addDate)
          }
          onClick={confirmAddNight}
          className="w-full rounded-full bg-[#455d3b] py-3 text-sm font-medium text-white active:scale-[0.99] transition disabled:opacity-60"
        >
          {/* DATE decides the CTA, never the clock (Mark, Aug 21: a right-
              now plan carries the decide moment, which is already seconds
              in the past by the time you read the button — comparing
              timestamps made the label flicker on the boundary). Today or
              future = "Done"; only genuinely past days = "Add to Been". */}
          {/* CTAs answer the title's question (Mark, Oct 10): I'm here /
              Make the plan / Add the night. */}
          {addSaving
            ? "Adding…"
            : isEvent
            ? "Create event"
            : addMode === "now"
            ? "I'm here"
            : addDate >= todayStr
            ? "Make the plan"
            : "Add it"}
        </button>
      </div>
    </div>,
    document.body
  );
}
